import { z } from 'zod';
import { slugSchema } from './common.js';

export const executeQueryRequestSchema = z.object({
  datasetSlug: slugSchema,
  sql: z.string().min(1).max(10_000),
});
export type ExecuteQueryRequest = z.infer<typeof executeQueryRequestSchema>;

export const resultColumnSchema = z.object({
  name: z.string(),
  /** PostgreSQL type name resolved from the result field's OID. */
  dataType: z.string(),
});
export type ResultColumn = z.infer<typeof resultColumnSchema>;

/**
 * Cell values are pre-serialised to string | null by the API. Sending raw JSON
 * numbers would silently lose precision on `numeric` and `bigint`, which is
 * unacceptable in a tool whose entire job is showing correct query results.
 */
export const resultRowSchema = z.array(z.string().nullable());

export const queryResultSchema = z.object({
  columns: z.array(resultColumnSchema),
  rows: z.array(resultRowSchema),
  rowCount: z.number().int().nonnegative(),
  /** True when the result was cut off at the row cap. */
  truncated: z.boolean(),
  executionMs: z.number().int().nonnegative(),
});
export type QueryResult = z.infer<typeof queryResultSchema>;

/**
 * A PostgreSQL error rewritten for a learner. `raw` is always retained: an
 * analyst preparing for the job needs to become fluent in real error text.
 */
export const friendlyErrorSchema = z.object({
  title: z.string(),
  explanation: z.string(),
  suggestion: z.string().nullable(),
  /** Column or table names that do exist, when we can determine them. */
  didYouMean: z.array(z.string()).default([]),
  sqlState: z.string().nullable(),
  raw: z.string(),
  position: z.number().int().nullable(),
});
export type FriendlyError = z.infer<typeof friendlyErrorSchema>;

export const executeQueryResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('ok'), result: queryResultSchema }),
  z.object({ status: z.literal('error'), error: friendlyErrorSchema }),
]);
export type ExecuteQueryResponse = z.infer<typeof executeQueryResponseSchema>;
